import {
  InputAdornment,
  InputProps,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { forwardRef, useCallback, useState } from "react";
import { HFieldMapProps } from "../types";
import { HLabelAdornment } from "./HLabel";
import { HTooltipSuffix } from "./HTooltip";
import { HintSuffix } from "./HintSuffix";
import { HypeIcon } from "./HypeIcon";

export type HTextFieldProps = HFieldMapProps<TextFieldProps>;

export const HTextField = forwardRef<HTMLInputElement, HTextFieldProps>(
  (props, ref) => {
    const {
      hint,
      tooltip,
      taxFieldModal,
      twoColumnsOptions,
      helpText,
      readonly = false,
      type,
      InputProps: inputComponentProps,
      maxLength,
      textControlPattern,
      fieldPrefix,
      fieldSuffix,
      ...rest
    } = props;

    const [eyeIconIsHidden, setEyeIconIsHidden] = useState(false);
    const toggleEyeIcon = useCallback(() => {
      setEyeIconIsHidden(s => !s);
    }, []);

    const hasEyeIcon = type === "password";

    return (
      <TextField
        variant="standard"
        fullWidth
        helperText={helpText}
        type={!eyeIconIsHidden ? props.type : "text"}
        inputProps={{
          maxLength,
          ...rest.inputProps,
        }}
        InputProps={
          {
            inputMode: !eyeIconIsHidden ? props.type : "text",
            startAdornment: fieldPrefix && (
              <InputAdornment position="start" data-testid="start-adornment">
                <HLabelAdornment separatorPosition="end" {...fieldPrefix} />
              </InputAdornment>
            ),
            endAdornment: fieldSuffix ? (
              <InputAdornment position="end">
                <HLabelAdornment separatorPosition="start" {...fieldSuffix} />
              </InputAdornment>
            ) : hint || hasEyeIcon ? (
              <InputAdornment position="end" data-testid="adornment">
                {hasEyeIcon && (
                  <HypeIcon
                    data-testid="eye-icon"
                    size={24}
                    onClick={toggleEyeIcon}
                    name={eyeIconIsHidden ? "icon_eye_hide" : "icon_eye"}
                  />
                )}
                {hint && <HintSuffix {...hint} />}
              </InputAdornment>
            ) : tooltip ? (
              <HTooltipSuffix {...tooltip} />
            ) : null,
            readOnly: readonly,
            ...inputComponentProps,
          } as Partial<InputProps>
        }
        InputLabelProps={{ shrink: true, ...rest.InputLabelProps }}
        {...rest}
        inputRef={ref}
      />
    );
  },
);
